/**
 * API 工具函数
 * 处理与后端 API 的通信
 */

import type { 
  ApiResponse, 
  SearchResult, 
  SearchParams, 
  BatchDeleteParams, 
  BatchDeleteResult,
  RealtimeStats,
  TimeSeriesData,
  KeyStats
} from '../types';

// API 基础配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const DEFAULT_TIMEOUT = 10000; // 10秒超时



/**
 * 创建带有代理的 fetch 函数
 */
function createFetch() {
  // 在开发环境中使用代理
  if (import.meta.env.DEV) {
    return async (url: string, options?: RequestInit): Promise<Response> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
          },
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    };
  }

  // 生产环境使用标准 fetch
  return fetch;
}

const apiClient = createFetch();

/**
 * API 错误类
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 处理 API 响应
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: any = {};
    try {
      errorData = await response.json();
    } catch {
      // 忽略 JSON 解析错误
    }

    throw new ApiError(
      response.status,
      errorData.code || 'UNKNOWN_ERROR',
      errorData.message || `HTTP ${response.status}: ${response.statusText}`,
      errorData.data
    );
  }

  try {
    return await response.json();
  } catch (error) {
    throw new ApiError(
      500,
      'JSON_PARSE_ERROR',
      'Failed to parse response JSON'
    );
  }
}

/**
 * 构建查询字符串
 */
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * 发送数据到接收端点
 */
export async function sendData(
  data: string, 
  contentType: string
): Promise<ApiResponse<{ id: string }>> {
  const response = await apiClient(`${API_BASE_URL}/receive`, {
    method: 'POST',
    headers: {
      'Content-Type': contentType,
    },
    body: data,
  });

  return handleResponse<ApiResponse<{ id: string }>>(response);
}

/**
 * 搜索数据
 */
export async function searchData(params: SearchParams): Promise<ApiResponse<SearchResult>> {
  const queryString = buildQueryString(params);
  const response = await apiClient(`${API_BASE_URL}/search${queryString}`);
  
  return handleResponse<ApiResponse<SearchResult>>(response);
}

/**
 * 获取搜索建议
 */
export async function getSearchSuggestions(
  query: string, 
  limit: number = 10
): Promise<ApiResponse<{ suggestions: { keys: string[]; values: string[] } }>> {
  const queryString = buildQueryString({ q: query, limit });
  const response = await apiClient(`${API_BASE_URL}/search/suggestions${queryString}`);
  
  return handleResponse<ApiResponse<{ suggestions: { keys: string[]; values: string[] } }>>(response);
}

/**
 * 获取所有键名
 */
export async function getKeys(): Promise<ApiResponse<{ keys: string[] }>> {
  const response = await apiClient(`${API_BASE_URL}/keys`);
  return handleResponse<ApiResponse<{ keys: string[] }>>(response);
}

/**
 * 获取键名统计
 */
export async function getKeysStats(): Promise<ApiResponse<{
  summary: any;
  topKeys: KeyStats[];
  allKeys: KeyStats[];
}>> {
  const response = await apiClient(`${API_BASE_URL}/keys/stats`);
  return handleResponse(response);
}

/**
 * 搜索键名
 */
export async function searchKeys(
  query: string, 
  limit: number = 50
): Promise<ApiResponse<{
  query: string;
  matches: number;
  keys: Array<{ key: string; usageCount: number }>;
}>> {
  const queryString = buildQueryString({ q: query, limit });
  const response = await apiClient(`${API_BASE_URL}/keys/search${queryString}`);
  
  return handleResponse(response);
}

/**
 * 获取总记录数
 */
export async function getCount(): Promise<ApiResponse<{ total: number }>> {
  const response = await apiClient(`${API_BASE_URL}/count`);
  return handleResponse<ApiResponse<{ total: number }>>(response);
}

/**
 * 获取统计信息
 */
export async function getStats(): Promise<ApiResponse<any>> {
  const response = await apiClient(`${API_BASE_URL}/stats`);
  return handleResponse<ApiResponse<any>>(response);
}

/**
 * 获取实时统计信息
 */
export async function getRealtimeStats(): Promise<ApiResponse<RealtimeStats>> {
  const response = await apiClient(`${API_BASE_URL}/stats/realtime`);
  return handleResponse<ApiResponse<RealtimeStats>>(response);
}

/**
 * 获取时间序列统计
 */
export async function getTimeSeriesStats(
  period: '24h' | '7d' | '30d' = '24h'
): Promise<ApiResponse<TimeSeriesData>> {
  const queryString = buildQueryString({ period });
  const response = await apiClient(`${API_BASE_URL}/stats/timeseries${queryString}`);
  
  return handleResponse<ApiResponse<TimeSeriesData>>(response);
}

/**
 * 批量删除数据
 */
export async function batchDelete(params: BatchDeleteParams): Promise<ApiResponse<BatchDeleteResult>> {
  const response = await apiClient(`${API_BASE_URL}/batch-delete`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  return handleResponse<ApiResponse<BatchDeleteResult>>(response);
}

/**
 * 删除单个记录
 */
export async function deleteSingle(id: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> {
  const response = await apiClient(`${API_BASE_URL}/records/${id}`, {
    method: 'DELETE',
  });

  return handleResponse<ApiResponse<{ id: string; deleted: boolean }>>(response);
}

/**
 * 删除所有数据（危险操作）
 */
export async function deleteAll(): Promise<ApiResponse<{ deleted: number }>> {
  const response = await apiClient(`${API_BASE_URL}/all?confirm=DELETE_ALL_DATA`, {
    method: 'DELETE',
    headers: {
      'X-Confirm-Delete-All': 'true',
    },
  });

  return handleResponse<ApiResponse<{ deleted: number }>>(response);
}

/**
 * 健康检查
 */
export async function healthCheck(): Promise<ApiResponse<any>> {
  const response = await apiClient(`${API_BASE_URL.replace('/api', '')}/`);
  return handleResponse<ApiResponse<any>>(response);
}

/**
 * 重试机制包装器
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      // 如果是最后一次重试，直接抛出错误
      if (i === maxRetries) {
        throw lastError;
      }

      // 如果是客户端错误（4xx），不重试
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        throw error;
      }

      // 等待后重试
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }

  throw lastError!;
}

/**
 * 批量请求处理器
 */
export async function batchRequest<T>(
  requests: Array<() => Promise<T>>,
  concurrency: number = 5
): Promise<Array<T | Error>> {
  const results: Array<T | Error> = [];
  
  for (let i = 0; i < requests.length; i += concurrency) {
    const batch = requests.slice(i, i + concurrency);
    const batchResults = await Promise.allSettled(
      batch.map(request => request())
    );
    
    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        results.push(result.value);
      } else {
        results.push(result.reason);
      }
    });
  }
  
  return results;
}